import {
	createDateRangeArray,
	createScheduleDayParser,
	createScheduleDayParserFromUser,
	getActiveSchedule,
} from "@app/_helpers/utils";
import { UserSchedule, UserSettings } from "@timeghost/web-api";
import { startOfDay } from "date-fns/esm";
import { indexByStartOfDay } from "./reports-chart-series";

export type ReportsScheduleStatsAcc = {
	used: number;
	max: number;
	schedule: UserSchedule | null;
};

export type ReportsScheduleStatsResult = ReportsScheduleStatsAcc & {
	total: ReportsScheduleStatsAcc;
	isPastSchedule: boolean;
	pastFailure: boolean;
	graphState: "primary" | "accent" | "success";
};

export type ScheduleStatsTimeItem = {
	start: string | number | Date;
	duration: number;
	timeDiff?: number;
};

/**
 * Aggregate schedule used/max over [from, to] using day-indexed times.
 * O(times + days) instead of O(days * times).
 */
export function computeReportsScheduleStats(input: {
	times: ScheduleStatsTimeItem[] | null | undefined;
	user: UserSettings;
	from: Date;
	to: Date;
	now?: Date;
}): ReportsScheduleStatsResult | null {
	const { times, user, from, to } = input;
	const now = input.now ?? new Date();
	const sched = getActiveSchedule(user, from, false);
	if (!sched || !sched.enabled) return null;
	const isPastSchedule = now.getTime() > startOfDay(to.getTime()).getTime();
	const rootParser = createScheduleDayParser(sched as any);
	if (!rootParser) return null;

	const allTimes = times ?? [];
	const timesByDay = indexByStartOfDay(allTimes, startOfDay);
	const stats = createDateRangeArray(from, to, false)[0].reduce(
		(acc, [d]) => {
			const dayParser = createScheduleDayParserFromUser(user, d);
			const dayTimes = timesByDay.get(startOfDay(d).getTime()) ?? [];
			const used = dayTimes.reduce((tcc, t) => (tcc += t.duration), 0);
			if (!dayParser) {
				acc.used += used;
				return acc;
			}
			const data = dayParser(dayTimes as any, d);
			if (!data?.enabled) {
				acc.used += used;
				return acc;
			}
			if (!acc.schedule) acc.schedule = data.schedule;
			acc.used += data.usage;
			acc.max += data.cap;
			return acc;
		},
		{ max: 0, used: 0, schedule: null } as ReportsScheduleStatsAcc
	);

	const pastFailure = isPastSchedule && stats.used < stats.max;
	return {
		...stats,
		total: { ...stats },
		isPastSchedule,
		pastFailure,
		graphState: pastFailure ? "primary" : stats.used < stats.max ? "accent" : "success",
	};
}
